import { NativeNavigation } from "@eventr/native/navigation";
import { create } from "zustand";
import { isIosNativeNavigation } from "./platform";

interface SplitState {
  /**
   * Whether the native splits show one column. Every pane tab is a split in the same window,
   * so they collapse and expand together and one flag stands for all of them. True until a
   * split reports otherwise, so nothing lays out two panes before there are two columns.
   */
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  /** Whether the presented tab bar's pane tabs are splits, which the two-pane setting decides. */
  tabsAreSplits: boolean;
  setTabsAreSplits: (tabsAreSplits: boolean) => void;
  /** Whether Home's guided create has the whole screen, edge to edge, folded or not. */
  trailingTakesScreen: boolean;
  setTrailingTakesScreen: (trailingTakesScreen: boolean) => void;
}

export const useSplitStore = create<SplitState>()((set) => ({
  collapsed: true,
  setCollapsed: (collapsed) => set({ collapsed }),
  tabsAreSplits: false,
  setTabsAreSplits: (tabsAreSplits) => set({ tabsAreSplits }),
  trailingTakesScreen: false,
  setTrailingTakesScreen: (trailingTakesScreen) => set({ trailingTakesScreen }),
}));

export const useSplitCollapsed = () => useSplitStore((s) => s.collapsed);

/** The trailing stack of the split a pane tab becomes. The leading one keeps the tab's own id. */
export const trailingStackId = (stackId: string) => `${stackId}.trailing`;

/** The split a pane tab becomes, which the tab bar knows the tab by. */
export const splitComponentId = (stackId: string) => `${stackId}.split`;

/** Whether `stackId` is a split's trailing stack. */
export const isTrailingStackId = (stackId: string | undefined): stackId is string =>
  !!stackId?.endsWith(".trailing");

/** The leading stack beside the trailing stack `stackId`, which is the tab's own stack. */
export const leadingStackIdOf = (stackId: string) => stackId.slice(0, -".trailing".length);

const ENTRY_SHEET_PREFIX = "entry-sheet-";

/** A fresh id each time: a sheet that is closing still holds its id until its webview goes. */
let entrySheetCount = 0;
export const nextEntrySheetStackId = () => `${ENTRY_SHEET_PREFIX}${++entrySheetCount}`;

/** Whether `stackId` is a sheet holding a pane tab's entry opened away from that tab. */
export const isEntrySheetStackId = (stackId: string | undefined): stackId is string =>
  !!stackId?.startsWith(ENTRY_SHEET_PREFIX);

let listening = false;

/** Follows the splits' collapse and expand events. Safe to call more than once. */
export function listenForSplitChanges(): void {
  if (listening || !isIosNativeNavigation()) return;
  listening = true;
  const { setCollapsed } = useSplitStore.getState();
  void NativeNavigation.addListener("splitCollapsed", () => setCollapsed(true));
  void NativeNavigation.addListener("splitExpanded", () => setCollapsed(false));
}

/**
 * Reads whether the split on screen is collapsed, for when a split has just been presented:
 * UIKit lays a new split out in the window's width without reporting a transition.
 */
export async function refreshSplitCollapsed(): Promise<void> {
  if (!isIosNativeNavigation()) return;
  try {
    const { split } = await NativeNavigation.get();
    if (split) useSplitStore.getState().setCollapsed(split.collapsed);
  } catch {
    // Nothing presented: nothing to read.
  }
}
